import {
    ActionButton,
    Badge,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { IWeeklyScheduleDashboard } from "../../../../../schedule/types/schedule.types";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../../utils/helpers";
import RefreshClockIcon from "../../../../../../components/Icons/RefreshClock";
import MultiplyCircleIcon from "../../../../../../components/Icons/MultiplyCircle";
import CancelSessionForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import EditSessionTimeForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/EditSessionTimeForm/EditSessionTimeForm";
import { RecurrenceRule } from "../../../../../../utils/types";
import useGetWeekViewData from "../../../../hooks/useGetWeekViewData";

interface WeekViewItemProps {
    schedule: IWeeklyScheduleDashboard;
}

type Dialogs = "cancel_session" | "reschedule_session";

export default function WeekViewItem({ schedule }: WeekViewItemProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);

    const { isWithinInterval } = useGetWeekViewData();

    const isCancelSessionMutating = useIsMutating(["cancel-session"]);
    const isEditTimeMutating = useIsMutating(["edit-session-time"]);

    const sessionStartTime =
        isWithinInterval && schedule.confirmed_start_date_time
            ? formatDate(
                  schedule.confirmed_start_date_time || "",
                  false,
                  "hh:mmaa"
              )
            : formatDate(
                  schedule.possible_start_date_time || "",
                  false,
                  "hh:mmaa"
              );

    const sessionEndTime =
        isWithinInterval && schedule.confirmed_end_date_time
            ? formatDate(
                  schedule.confirmed_end_date_time || "",
                  false,
                  "hh:mmaa"
              )
            : formatDate(
                  schedule.possible_end_date_time || "",
                  false,
                  "hh:mmaa"
              );

    const getCardBgColor = () => {
        switch (schedule.recurrence_rule) {
            case RecurrenceRule.MONTHLY:
                return "var(--bg-appointment-monthly)";

            case RecurrenceRule.WEEKLY:
                return "var(--bg-appointment-weekly)";

            case RecurrenceRule.BIWEEKLY:
                return "var(--bg-appointment-bi_weekly)";

            case RecurrenceRule.ONE_OFF:
                return "var(--bg-appointment-one_off)";

            case RecurrenceRule.TRIWEEKLY:
                return "var(--bg-appointment-tri_weekly)";

            case RecurrenceRule.TWO_MONTHS:
                return "var(--bg-appointment-two_months)";

            case RecurrenceRule.THREE_MONTHS:
                return "var(--bg-appointment-three_months)";

            default:
                return "var(--bg-appointment-three_months)";
        }
    };

    return (
        <div>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => setDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    canCancelButtonSubmit
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => setDialogInView(null)}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={schedule.session_history_id || null}
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            {schedule.session_history_id &&
            schedule.start_date_time &&
            schedule.end_date_time ? (
                <Dialog open={dialogInView === "reschedule_session"}>
                    <DialogContent
                        title="Edit Session Time"
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={isEditTimeMutating > 0}
                        isSubmitBtnDisabled={isEditTimeMutating > 0}
                        showFooter
                        submitBtnFormValue="edit-session-time"
                    >
                        <EditSessionTimeForm
                            sessionHistoryId={schedule.session_history_id}
                            onFormSubmit={() => {
                                setDialogInView(null);
                            }}
                            sessionStartDateTime={schedule.start_date_time}
                            sessionEndDateTime={schedule.end_date_time}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}

            <div
                className="h-[168px] p-8 rounded-md [backdrop-filter:blur(12px)] flex flex-col gap-y-1 relative group text-black"
                style={{
                    backgroundColor: getCardBgColor(),
                }}
            >
                <span className="text-caption font-medium text-dark">
                    {displayNameInRightFormat({
                        firstName: schedule.client.first_name,
                        lastName: schedule.client.last_name,
                    })}
                </span>
                <span className="text-dark opacity-50 text-[11px] font-medium tracking-[0.22px] uppercase">
                    {`${sessionStartTime} - ${sessionEndTime}`}
                </span>
                <span className="uppercase text-dark opacity-50  text-[11px] font-medium  tracking-[0.22px]">
                    {makeStringFirstLetterCapital(
                        removeEnumUnderscore(schedule.service)
                    )}
                </span>
                <span className="uppercase text-dark opacity-50  text-[11px] font-medium] tracking-[0.22px]">
                    {schedule.client.insurance_provider_name
                        ? removeEnumUnderscore(
                              makeStringFirstLetterCapital(
                                  schedule.client.insurance_provider_name
                              )
                          )
                        : "SELF PAY"}
                </span>
                <div className="mt-auto">
                    <div className="flex flex-col items-start">
                        {schedule.is_paid ? (
                            <Badge content="Paid" type="success" />
                        ) : (
                            <Badge content="Unpaid" type="danger" />
                        )}
                    </div>
                    <div className="px-2 py-1 min-h-7 flex items-center gap-x-1.5 rounded-md bg-white [border] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] flex-wrap gap-y-2 justify-self-start mt-2">
                        {schedule.session_history_id &&
                        schedule.start_date_time &&
                        schedule.end_date_time ? (
                            <ActionButton
                                size="auto"
                                variant="normal"
                                className="px-6 h-6 hover:bg-[#e7e8ec]"
                                onClick={() =>
                                    setDialogInView("reschedule_session")
                                }
                                actionTitle="Reschedule session"
                            >
                                <RefreshClockIcon />
                            </ActionButton>
                        ) : null}

                        <ActionButton
                            size="auto"
                            variant="normal"
                            className="px-6 h-6 hover:bg-[#e7e8ec]"
                            onClick={() => setDialogInView("cancel_session")}
                            actionTitle="Cancel session"
                        >
                            <MultiplyCircleIcon />
                        </ActionButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
